import React, { useState } from 'react';
import { PencilIcon, TrashIcon, ArrowsRightLeftIcon } from '@heroicons/react/24/outline';
import type {
  MovimentacaoItemIntake,
  MovimentacaoItemExecucao,
  MovimentacaoEquipamentoView,
  TipoEquipamentoMovimentacao,
  SistemaOperacionalMovimentacao,
  PropriedadeEquipamento,
  TipoFluxoMovimentacao,
} from '../../../models';
import {
  TIPO_EQUIPAMENTO_LABELS,
  SISTEMA_OPERACIONAL_LABELS,
  PROPRIEDADE_EQUIPAMENTO_OPCOES,
  labelPropriedadeEquipamento,
  SO_OPCOES_POR_TIPO_EQUIPAMENTO,
  SO_IMPLICITO_POR_TIPO_EQUIPAMENTO,
  TIPOS_EQUIPAMENTO_COM_HOMOLOGACAO,
  TIPOS_EQUIPAMENTO_COM_SENHA,
} from '../solicitacaoDetailsPage.definitions';
import { useMovimentacaoMutations } from '../../../hooks/data';
import VincularEquipamentoSection from './VincularEquipamentoSection';
import SugestaoEstadiaClienteSection from './SugestaoEstadiaClienteSection';
import HistoricoEstadiasEquipamentoSection from './HistoricoEstadiasEquipamentoSection';
import FotosEquipamentoSection from './FotosEquipamentoSection';
import CondicaoEquipamentoSection from './CondicaoEquipamentoSection';

interface MovimentacaoEquipamentoItemProps {
  solicitacaoId: string;
  movimentacaoId: string;
  equipamentosIntake: MovimentacaoItemIntake[];
  equipamentosExecucao: MovimentacaoItemExecucao[];
  item: MovimentacaoEquipamentoView;
  podeEditar: boolean;
  /** SI ou o solicitante — pode ver a senha (mas só `podeEditar`, ou seja, só SI de
   * verdade, consegue editar/registrar). Independente da aba/visão forçada. */
  podeVerSenha: boolean;
  clienteNome: string | null;
  /** Cliente/colaborador desta Movimentação — ver SugestaoEstadiaClienteSection. */
  clienteId: string | null;
  colaboradorId: string | null;
  /** Ver SolicitacaoDetailsContent — só alguns Clientes permitem equipamento próprio. */
  propriedadeVisivel: boolean;
  /** Ver SolicitacaoDetailsContent — só alguns Clientes usam máquina homologada. */
  homologadoVisivel: boolean;
  /** Define quais opções de Condição aparecem (envio: Novo/Usado; devolução: Ótima/Boa/
   * Péssima/Com defeito) — ver CONDICAO_EQUIPAMENTO_*_OPCOES. */
  tipoFluxo: TipoFluxoMovimentacao;
  /** false = item ainda não existe em equipamentosIntake (placeholder do "1º equipamento"
   * quando a Movimentação nasceu sem nenhum, ex. desligamento) — salvar o tipo cria a
   * entrada em vez de atualizar uma já existente. */
  existeNoIntake: boolean;
  /** Ausente quando não faz sentido remover (ex. placeholder que nem foi salvo ainda). */
  onRemover?: () => void;
  /** Só faz sentido quando a Movimentação tem mais de 1 equipamento — separar o único
   * item existente não muda nada. Ex.: monitor + notebook nasceram juntos, mas
   * normalmente não dá pra mandar/devolver junto (embalagem/transporte diferentes). */
  podeMover: boolean;
}

/**
 * Um item de equipamento dentro de uma Movimentação (que pode ter mais de um — ex.
 * notebook + monitor no mesmo envio). Extraído do MovimentacaoCard pra manter estado
 * local (inputs de "tipo/SO/homologado ainda não preenchidos") isolado por item. Edita
 * `equipamentosIntake` (tipo/SO/homologado/propriedade); o vínculo de patrimônio/nº
 * série fica a cargo de VincularEquipamentoSection, que edita `equipamentosExecucao`.
 *
 * Mesmo depois de já preenchido, o SI pode reabrir o formulário (ícone de lápis) — raro,
 * mas a solicitação pode mudar depois de criada.
 */
export default function MovimentacaoEquipamentoItem({
  solicitacaoId,
  movimentacaoId,
  equipamentosIntake,
  equipamentosExecucao,
  item,
  podeEditar,
  podeVerSenha,
  clienteNome,
  clienteId,
  colaboradorId,
  propriedadeVisivel,
  homologadoVisivel: permiteHomologado,
  tipoFluxo,
  existeNoIntake,
  onRemover,
  podeMover,
}: MovimentacaoEquipamentoItemProps) {
  const { atualizarMovimentacao, moverEquipamentoParaNovaMovimentacao } = useMovimentacaoMutations(solicitacaoId);

  const [editando, setEditando] = useState(false);
  const [tipoInput, setTipoInput] = useState<TipoEquipamentoMovimentacao | ''>('');
  const [soInput, setSoInput] = useState<SistemaOperacionalMovimentacao | ''>('');
  const [homologadoInput, setHomologadoInput] = useState(false);
  const [propriedadeInput, setPropriedadeInput] = useState<PropriedadeEquipamento | ''>('');

  const [editandoSenha, setEditandoSenha] = useState(false);
  const [senhaInput, setSenhaInput] = useState('');

  const mostrarFormulario = podeEditar && (!item.tipoEquipamento || editando);
  // Por item (não por Movimentação inteira) — cada equipamento tem seu próprio patrimônio
  // no GLPI. Rótulo varia por tipoFluxo: no envio é "vincular" o equipamento ao
  // colaborador; na devolução é "desvincular" (ele está voltando).
  const labelGlpi = tipoFluxo === 'envio' ? 'Vinculado no GLPI' : 'Desvinculado no GLPI';

  const handleToggleGlpi = () => {
    const novoValor = !(item.glpiAtualizado ?? false);
    const jaTinhaEntrada = equipamentosExecucao.some((equipamento) => equipamento.id === item.id);
    const novosExecucao = jaTinhaEntrada
      ? equipamentosExecucao.map((equipamento) =>
          equipamento.id === item.id ? { ...equipamento, glpiAtualizado: novoValor } : equipamento
        )
      : [...equipamentosExecucao, { id: item.id, glpiAtualizado: novoValor }];
    atualizarMovimentacao.mutate({ id: movimentacaoId, equipamentosExecucao: novosExecucao });
  };

  // Periférico e cartão de acesso são sempre da Raro — não pergunta (grava 'raro').
  const perguntaPropriedade =
    propriedadeVisivel && tipoInput !== 'periferico' && tipoInput !== 'cartao_acesso';

  const handleMoverParaNovaMovimentacao = () => {
    moverEquipamentoParaNovaMovimentacao.mutate({ movimentacaoOrigemId: movimentacaoId, itemId: item.id });
  };
  // Senha só faz sentido no envio (colaborador precisa saber como acessar o equipamento
  // que está recebendo) — na devolução não há por que registrar/mostrar isso. Equipamento
  // homologado nunca tem o campo exibido: nesses casos a Raro não recebe a senha, o
  // colaborador recebe direto do fornecedor (mesma exceção já aplicada em
  // pendenciaMovimentacao/concluirSolicitacao — aqui só reflete isso na UI).
  const temSenha =
    tipoFluxo === 'envio' &&
    podeVerSenha &&
    !item.homologado &&
    item.tipoEquipamento &&
    TIPOS_EQUIPAMENTO_COM_SENHA.includes(item.tipoEquipamento);

  const soOpcoes = tipoInput ? SO_OPCOES_POR_TIPO_EQUIPAMENTO[tipoInput] : undefined;
  const soVisivel = Boolean(soOpcoes);
  const soImplicito = tipoInput ? SO_IMPLICITO_POR_TIPO_EQUIPAMENTO[tipoInput] : undefined;
  const homologadoVisivel =
    permiteHomologado &&
    Boolean(tipoInput) &&
    TIPOS_EQUIPAMENTO_COM_HOMOLOGACAO.includes(tipoInput as TipoEquipamentoMovimentacao);

  const handleAbrirEdicao = () => {
    setTipoInput(item.tipoEquipamento ?? '');
    setSoInput(item.sistemaOperacional ?? '');
    setHomologadoInput(item.homologado ?? false);
    setPropriedadeInput(item.propriedadeEquipamento ?? '');
    setEditando(true);
  };

  const handleCompletarTipo = () => {
    if (!tipoInput) return;
    const dadosItem = {
      tipoEquipamento: tipoInput,
      sistemaOperacional: soVisivel ? (soInput as SistemaOperacionalMovimentacao) || null : soImplicito ?? null,
      homologado: homologadoVisivel ? homologadoInput : null,
      // Cliente sem regra habilitada (ou periférico/cartão): sempre Raro, nem pergunta.
      propriedadeEquipamento: perguntaPropriedade ? propriedadeInput || null : 'raro',
    };
    const novosIntake = existeNoIntake
      ? equipamentosIntake.map((equipamento) =>
          equipamento.id === item.id ? { ...equipamento, ...dadosItem } : equipamento
        )
      : [...equipamentosIntake, { id: item.id, ...dadosItem }];
    atualizarMovimentacao.mutate({ id: movimentacaoId, equipamentosIntake: novosIntake });
    setEditando(false);
  };

  const handleAbrirEdicaoSenha = () => {
    setSenhaInput(item.senha ?? '');
    setEditandoSenha(true);
  };

  const handleSalvarSenha = () => {
    const valor = senhaInput.trim() || null;
    const jaTinhaEntrada = equipamentosExecucao.some((equipamento) => equipamento.id === item.id);
    const novosExecucao = jaTinhaEntrada
      ? equipamentosExecucao.map((equipamento) =>
          equipamento.id === item.id ? { ...equipamento, senha: valor } : equipamento
        )
      : [...equipamentosExecucao, { id: item.id, senha: valor }];
    atualizarMovimentacao.mutate({ id: movimentacaoId, equipamentosExecucao: novosExecucao });
    setEditandoSenha(false);
  };

  return (
    <div>
      {!mostrarFormulario ? (
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="font-montserrat text-lg font-semibold text-gray-900">
              {item.tipoEquipamento ? TIPO_EQUIPAMENTO_LABELS[item.tipoEquipamento] : 'Equipamento a definir'}
            </h3>
            <p className="mt-0.5 text-sm text-gray-600">
              {[
                item.sistemaOperacional ? SISTEMA_OPERACIONAL_LABELS[item.sistemaOperacional] : null,
                typeof item.homologado === 'boolean' ? (item.homologado ? 'Homologado' : 'Não homologado') : null,
                item.propriedadeEquipamento
                  ? `Propriedade ${labelPropriedadeEquipamento(item.propriedadeEquipamento, clienteNome)}`
                  : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
            {item.descricaoLivre && (
              <p className="mt-1 text-sm italic text-gray-500">Descrito pelo solicitante: &quot;{item.descricaoLivre}&quot;</p>
            )}
          </div>
          {podeEditar && (
            <div className="flex shrink-0 gap-2">
              <button
                type="button"
                className="text-gray-400 hover:text-gray-600"
                onClick={handleAbrirEdicao}
                title="Editar equipamento"
              >
                <PencilIcon className="h-4 w-4" />
              </button>
              {podeMover && existeNoIntake && (
                <button
                  type="button"
                  className="text-gray-400 hover:text-gray-600"
                  onClick={handleMoverParaNovaMovimentacao}
                  disabled={moverEquipamentoParaNovaMovimentacao.isPending}
                  title="Mover para uma nova Movimentação (ex.: logística separada)"
                >
                  <ArrowsRightLeftIcon className="h-4 w-4" />
                </button>
              )}
              {onRemover && (
                <button
                  type="button"
                  className="text-gray-400 hover:text-red-600"
                  onClick={onRemover}
                  title="Remover equipamento"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <select
              className="input-gaveta w-full"
              value={tipoInput}
              onChange={(event) => {
                setTipoInput(event.target.value as TipoEquipamentoMovimentacao);
                setSoInput('');
                setHomologadoInput(false);
              }}
            >
              <option value="">Tipo de equipamento...</option>
              {Object.entries(TIPO_EQUIPAMENTO_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            {onRemover && (
              <button
                type="button"
                className="ml-2 shrink-0 text-gray-400 hover:text-red-600"
                onClick={onRemover}
                title="Remover equipamento"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            )}
          </div>
          {soVisivel && (
            <select
              className="input-gaveta w-full"
              value={soInput}
              onChange={(event) => setSoInput(event.target.value as SistemaOperacionalMovimentacao)}
            >
              <option value="">Sistema operacional...</option>
              {(soOpcoes ?? []).map((value) => (
                <option key={value} value={value}>
                  {SISTEMA_OPERACIONAL_LABELS[value]}
                </option>
              ))}
            </select>
          )}
          {homologadoVisivel && (
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                checked={homologadoInput}
                onChange={(event) => setHomologadoInput(event.target.checked)}
              />
              Equipamento homologado
            </label>
          )}
          {perguntaPropriedade && (
            <div>
              <p className="text-sm font-medium text-gray-700">Propriedade do Equipamento</p>
              <div className="mt-1 flex gap-4">
                {PROPRIEDADE_EQUIPAMENTO_OPCOES.map((value) => (
                  <label key={value} className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="radio"
                      checked={propriedadeInput === value}
                      onChange={() => setPropriedadeInput(value)}
                    />
                    {labelPropriedadeEquipamento(value, clienteNome)}
                  </label>
                ))}
              </div>
            </div>
          )}
          <div className="flex gap-2">
            <button type="button" className="btn-secondary text-sm" onClick={handleCompletarTipo} disabled={!tipoInput}>
              Salvar Equipamento
            </button>
            {editando && item.tipoEquipamento && (
              <button type="button" className="text-sm text-gray-500 underline" onClick={() => setEditando(false)}>
                Cancelar
              </button>
            )}
          </div>
        </div>
      )}

      {podeEditar && !mostrarFormulario && tipoFluxo === 'envio' && !item.equipamentoId && clienteId && (
        <SugestaoEstadiaClienteSection
          solicitacaoId={solicitacaoId}
          movimentacaoId={movimentacaoId}
          equipamentosIntake={equipamentosIntake}
          item={item}
          clienteId={clienteId}
          colaboradorId={colaboradorId}
        />
      )}

      {podeEditar && !mostrarFormulario && (
        <VincularEquipamentoSection
          solicitacaoId={solicitacaoId}
          movimentacaoId={movimentacaoId}
          equipamentosExecucao={equipamentosExecucao}
          equipamentosIntake={equipamentosIntake}
          item={item}
          tipoFluxo={tipoFluxo}
        />
      )}

      {!mostrarFormulario && item.equipamentoId && (
        <HistoricoEstadiasEquipamentoSection equipamentoId={item.equipamentoId} />
      )}

      {temSenha && !mostrarFormulario && (
        <div className="mt-4 border-t border-gray-100 pt-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Senha do equipamento</p>
          {!editandoSenha ? (
            <div className="mt-1 flex items-center justify-between gap-2">
              <p className="font-mono text-sm text-gray-900">{item.senha ?? '—'}</p>
              {podeEditar && (
                <button
                  type="button"
                  className="text-gray-400 hover:text-gray-600"
                  onClick={handleAbrirEdicaoSenha}
                  title="Editar senha"
                >
                  <PencilIcon className="h-4 w-4" />
                </button>
              )}
            </div>
          ) : (
            <div className="mt-1 flex gap-2">
              <input
                type="text"
                className="input-gaveta flex-1"
                placeholder="Senha do equipamento"
                value={senhaInput}
                onChange={(event) => setSenhaInput(event.target.value)}
              />
              <button type="button" className="btn-secondary text-sm" onClick={handleSalvarSenha}>
                Salvar
              </button>
            </div>
          )}
        </div>
      )}

      {item.equipamentoId && !mostrarFormulario && (
        <CondicaoEquipamentoSection
          solicitacaoId={solicitacaoId}
          movimentacaoId={movimentacaoId}
          equipamentosExecucao={equipamentosExecucao}
          item={item}
          podeEditar={podeEditar}
          tipoFluxo={tipoFluxo}
        />
      )}

      {existeNoIntake && !mostrarFormulario && (
        <FotosEquipamentoSection
          solicitacaoId={solicitacaoId}
          movimentacaoId={movimentacaoId}
          itemId={item.id}
          fotos={item.fotos ?? []}
          podeEditar={podeEditar}
        />
      )}

      {item.equipamentoId && !mostrarFormulario && podeEditar && (
        <label className="mt-4 flex items-center gap-2 border-t border-gray-100 pt-4 text-sm text-gray-700">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            checked={item.glpiAtualizado ?? false}
            onChange={handleToggleGlpi}
          />
          {labelGlpi}
        </label>
      )}
    </div>
  );
}
